import { isMobileAtom, useRecoil } from '@orcar/common-d2c';
import {
  CreditCard,
  Globe,
  IdCard,
  MapPin,
  Plane,
  Rocket,
  Star,
  Store,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import RainbowLogo from '@/assets/img/logo_rainbow-color-v2.svg';
import LogoOrcar from './LogoOrcar';

interface SplitLandingPageProps {
  onSelectKorea: () => void;
}

const SplitLandingPage = ({ onSelectKorea }: SplitLandingPageProps) => {
  const { recoilValue: isMobile } = useRecoil(isMobileAtom);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // 페이드인 효과
    setIsVisible(true);
  }, []);

  const handleSelectOrcar = () => {
    window.location.href = 'https://www.orcar.ai';
  };

  const handleSelectRainbow = () => {
    onSelectKorea();
  };

  return (
    <div
      className={`
      fixed left-0 top-0 z-[1000] flex
      h-screen w-screen
      ${isVisible ? 'animate-fade-in' : 'opacity-0'}
      ${isMobile ? 'flex-col' : 'flex-row'}
    `}
    >
      {/* Rainbow Section */}
      <div
        className={`
            animate-slide-in-left relative flex flex-[0.8] cursor-pointer
            items-center justify-center overflow-hidden bg-gradient-to-br
            from-slate-700 via-slate-800 to-slate-900 transition-all duration-300
            ease-out
            hover:from-slate-600 hover:via-slate-700 hover:to-slate-800
            ${isMobile ? 'min-h-0' : 'h-full'}
          `}
        onClick={handleSelectRainbow}
      >
        {/* 배경 패턴 */}
        <div className='absolute inset-0 opacity-10'>
          <div className='absolute left-0 top-0 size-full bg-gradient-to-br from-transparent via-white/5 to-transparent'></div>
          {/* 원형 패턴들 */}
          <div
            className={`absolute rounded-full bg-gradient-to-br from-white/10 to-transparent blur-2xl ${isMobile ? 'right-5 top-5 size-16' : 'right-10 top-10 size-32'}`}
          ></div>
          <div
            className={`absolute rounded-full bg-gradient-to-br from-white/10 to-transparent blur-xl ${isMobile ? 'bottom-5 left-5 size-12' : 'bottom-10 left-10 size-24'}`}
          ></div>
          <div
            className={`absolute rounded-full bg-gradient-to-br from-white/10 to-transparent blur-lg ${isMobile ? 'left-3 top-1/3 size-8' : 'left-8 top-1/3 size-20'}`}
          ></div>
          <div
            className={`absolute rounded-full bg-gradient-to-br from-white/10 to-transparent blur-lg ${isMobile ? 'bottom-1/4 right-8 size-6' : 'bottom-1/4 right-20 size-16'}`}
          ></div>

          {/* 기하학적 패턴 */}
          <div
            className={`absolute rotate-12 rounded-lg border border-white/5 ${isMobile ? 'right-1/4 top-1/4 size-12' : 'right-1/4 top-1/4 size-24'}`}
          ></div>
          <div
            className={`absolute -rotate-12 rounded-full border border-white/5 ${isMobile ? 'bottom-1/3 left-1/3 size-8' : 'bottom-1/3 left-1/3 size-20'}`}
          ></div>

          {/* 그리드 라인 */}
          <div className='absolute left-1/4 top-0 h-full w-px bg-gradient-to-b from-transparent via-white/10 to-transparent'></div>
          <div className='absolute right-1/4 top-0 h-full w-px bg-gradient-to-b from-transparent via-white/10 to-transparent'></div>
          <div className='absolute left-0 top-1/4 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent'></div>
          <div className='absolute bottom-1/4 left-0 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent'></div>
        </div>

        {/* 장식 테두리 */}
        <div className='pointer-events-none absolute inset-4 rounded-3xl border border-white/5'></div>
        <div className='pointer-events-none absolute inset-8 rounded-2xl border border-white/10'></div>

        <div
          className={`relative z-10 text-center text-white ${isMobile ? 'px-6 py-8' : 'px-8 py-12'}`}
        >
          <div
            className={`${isMobile ? 'mb-4' : 'mb-8'} relative flex items-center justify-center`}
          >
            {/* 장식 요소들 */}
            <div
              className={`absolute ${isMobile ? '-left-2 -top-2' : '-left-3 -top-3'} size-3 rounded-full bg-white/20`}
            ></div>
            <div
              className={`absolute ${isMobile ? '-right-2 -top-2' : '-right-3 -top-3'} size-2 rounded-full bg-white/20`}
            ></div>
            <div
              className={`absolute ${isMobile ? '-bottom-2 -left-2' : '-bottom-3 -left-3'} size-2 rounded-full bg-white/20`}
            ></div>
            <div
              className={`absolute ${isMobile ? '-bottom-2 -right-2' : '-bottom-3 -right-3'} size-3 rounded-full bg-white/20`}
            ></div>

            <div
              className={`relative flex items-center justify-center overflow-hidden rounded-2xl border border-white/60 bg-white/80 shadow-2xl backdrop-blur-lg ${isMobile ? 'px-6 py-4' : 'px-8 py-5'}`}
            >
              {/* 내부 장식 */}
              <div className='absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5'></div>
              <img
                src={RainbowLogo}
                alt='Rainbow Rent Car'
                className={`${isMobile ? 'h-8' : 'h-12'} relative z-10 w-auto brightness-110 contrast-125 drop-shadow-lg`}
              />
            </div>
          </div>
          <h3
            className={`${isMobile ? 'mb-4 text-sm' : 'mb-8 text-lg'} font-normal text-white text-opacity-90`}
          >
            Korea's Premium Car Rental
          </h3>
          <div
            className={`${isMobile ? 'mb-6 max-w-60 space-y-3 text-sm leading-relaxed' : 'mb-10 max-w-72 space-y-4 text-base leading-relaxed'} mx-auto text-left text-white text-opacity-90`}
          >
            <div className='flex items-center'>
              <div
                className={`${isMobile ? 'mr-3 size-6' : 'mr-4 size-8'} flex items-center justify-center rounded-lg border border-white/20 bg-white/20 backdrop-blur-sm`}
              >
                <MapPin
                  className={`${isMobile ? 'size-3' : 'size-4'} text-white`}
                />
              </div>
              Local service in Korean
            </div>
            <div className='flex items-center'>
              <div
                className={`${isMobile ? 'mr-3 size-6' : 'mr-4 size-8'} flex items-center justify-center rounded-lg border border-white/20 bg-white/20 backdrop-blur-sm`}
              >
                <Store
                  className={`${isMobile ? 'size-3' : 'size-4'} text-white`}
                />
              </div>
              On-site payment available
            </div>
          </div>
          <button
            className={`
              group rounded-2xl border-2 border-white/30
              bg-white/20 font-bold text-white
              backdrop-blur-sm transition-all duration-300
              hover:translate-y-[-2px] hover:border-white/50 hover:bg-white/30 hover:shadow-xl
              active:translate-y-0 active:shadow-lg
              ${isMobile ? 'px-6 py-3 text-base' : 'px-10 py-4 text-lg'}
            `}
          >
            <span className='flex items-center justify-center'>
              Continue to Rainbow
              <span className='ml-2 transition-transform duration-300 group-hover:translate-x-1'>
                →
              </span>
            </span>
          </button>
        </div>
      </div>

      {/* Orcar.ai Section */}
      <div
        className={`
            from-orcar-040 to-orcar-030 animate-slide-in-right relative
            flex flex-[1.2] cursor-pointer items-center
            justify-center overflow-hidden bg-gradient-to-br transition-all duration-300
            ease-out
            hover:scale-[1.02] hover:brightness-110
            ${isMobile ? 'min-h-0' : 'h-full'}
            font-suit font-medium
          `}
        onClick={handleSelectOrcar}
      >
        {/* Recommended 배지 */}
        <div
          className={`absolute ${isMobile ? 'right-4 top-4' : 'right-6 top-6'} z-20`}
        >
          <div className='text-orcar-060 flex items-center gap-1 rounded-full border border-white/50 bg-white/90 px-3 py-1 text-xs font-bold shadow-lg backdrop-blur-sm'>
            <Star className='size-3' />
            Recommended
          </div>
        </div>
        {/* 배경 패턴 */}
        <div className='absolute inset-0 opacity-10'>
          <div className='absolute left-0 top-0 size-full bg-gradient-to-br from-transparent via-white/5 to-transparent'></div>
          {/* 원형 패턴들 */}
          <div
            className={`absolute rounded-full bg-gradient-to-br from-white/10 to-transparent blur-2xl ${isMobile ? 'left-5 top-5 size-16' : 'left-10 top-10 size-32'}`}
          ></div>
          <div
            className={`absolute rounded-full bg-gradient-to-br from-white/10 to-transparent blur-xl ${isMobile ? 'bottom-5 right-5 size-12' : 'bottom-10 right-10 size-24'}`}
          ></div>
          <div
            className={`absolute rounded-full bg-gradient-to-br from-white/10 to-transparent blur-lg ${isMobile ? 'right-3 top-1/3 size-8' : 'right-8 top-1/3 size-20'}`}
          ></div>
          <div
            className={`absolute rounded-full bg-gradient-to-br from-white/10 to-transparent blur-lg ${isMobile ? 'bottom-1/4 left-8 size-6' : 'bottom-1/4 left-20 size-16'}`}
          ></div>

          {/* 기하학적 패턴 */}
          <div
            className={`absolute -rotate-12 rounded-lg border border-white/5 ${isMobile ? 'left-1/4 top-1/4 size-12' : 'left-1/4 top-1/4 size-24'}`}
          ></div>
          <div
            className={`absolute rotate-12 rounded-full border border-white/5 ${isMobile ? 'bottom-1/3 right-1/3 size-8' : 'bottom-1/3 right-1/3 size-20'}`}
          ></div>

          {/* 그리드 라인 */}
          <div className='absolute left-1/4 top-0 h-full w-px bg-gradient-to-b from-transparent via-white/10 to-transparent'></div>
          <div className='absolute right-1/4 top-0 h-full w-px bg-gradient-to-b from-transparent via-white/10 to-transparent'></div>
          <div className='absolute left-0 top-1/4 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent'></div>
          <div className='absolute bottom-1/4 left-0 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent'></div>
        </div>

        {/* 장식 테두리 */}
        <div className='pointer-events-none absolute inset-4 rounded-3xl border border-white/5'></div>
        <div className='pointer-events-none absolute inset-8 rounded-2xl border border-white/10'></div>

        <div
          className={`relative z-10 text-center text-white ${isMobile ? 'px-6 py-8' : 'px-8 py-12'}`}
        >
          <div
            className={`${isMobile ? 'mb-4' : 'mb-8'} relative flex items-center justify-center`}
          >
            {/* 장식 요소들 */}
            <div
              className={`absolute ${isMobile ? '-left-2 -top-2' : '-left-3 -top-3'} size-3 rounded-full bg-white/20`}
            ></div>
            <div
              className={`absolute ${isMobile ? '-right-2 -top-2' : '-right-3 -top-3'} size-2 rounded-full bg-white/20`}
            ></div>
            <div
              className={`absolute ${isMobile ? '-bottom-2 -left-2' : '-bottom-3 -left-3'} size-2 rounded-full bg-white/20`}
            ></div>
            <div
              className={`absolute ${isMobile ? '-bottom-2 -right-2' : '-bottom-3 -right-3'} size-3 rounded-full bg-white/20`}
            ></div>

            <div
              className={`relative flex items-center justify-center overflow-hidden rounded-2xl border border-white/60 bg-white/80 shadow-2xl backdrop-blur-lg ${isMobile ? 'px-6 py-4' : 'px-8 py-5'}`}
            >
              {/* 내부 장식 */}
              <div className='absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5'></div>
              <LogoOrcar
                className={`${isMobile ? 'h-8' : 'h-12'} relative z-10 text-[#333333] drop-shadow-lg`}
              />
            </div>
          </div>
          <h3
            className={`${isMobile ? 'mb-4 text-base' : 'mb-8 text-xl'} flex items-center justify-center gap-2 font-normal text-white text-opacity-90`}
          >
            <Star className={`${isMobile ? 'size-4' : 'size-5'} text-white`} />
            Perfect for International Travelers
          </h3>
          <div
            className={`${isMobile ? 'mb-6 max-w-64 space-y-3 text-sm leading-relaxed' : 'mb-10 max-w-80 space-y-4 text-base leading-relaxed'} mx-auto text-left text-white text-opacity-95`}
          >
            <div className='flex items-center'>
              <div
                className={`${isMobile ? 'mr-3 size-6' : 'mr-4 size-8'} flex items-center justify-center rounded-lg border border-white/20 bg-white/20 backdrop-blur-sm`}
              >
                <Globe
                  className={`${isMobile ? 'size-3' : 'size-4'} text-white`}
                />
              </div>
              <span className='font-medium'>Multi-language support</span>
            </div>
            <div className='flex items-center'>
              <div
                className={`${isMobile ? 'mr-3 size-6' : 'mr-4 size-8'} flex items-center justify-center rounded-lg border border-white/20 bg-white/20 backdrop-blur-sm`}
              >
                <CreditCard
                  className={`${isMobile ? 'size-3' : 'size-4'} text-white`}
                />
              </div>
              <span className='font-medium'>Global payment options</span>
            </div>
            <div className='flex items-center'>
              <div
                className={`${isMobile ? 'mr-3 size-6' : 'mr-4 size-8'} flex items-center justify-center rounded-lg border border-white/20 bg-white/20 backdrop-blur-sm`}
              >
                <IdCard
                  className={`${isMobile ? 'size-3' : 'size-4'} text-white`}
                />
              </div>
              <span className='font-medium'>International driving license</span>
            </div>
            <div className='flex items-center'>
              <div
                className={`${isMobile ? 'mr-3 size-6' : 'mr-4 size-8'} flex items-center justify-center rounded-lg border border-white/20 bg-white/20 backdrop-blur-sm`}
              >
                <Plane
                  className={`${isMobile ? 'size-3' : 'size-4'} text-white`}
                />
              </div>
              <span className='font-medium'>Tourist-friendly service</span>
            </div>
          </div>
          <button
            className={`
              text-orcar-060 hover:shadow-orcar-050/30 group rounded-2xl
              border-2 border-white/50 bg-white
              font-bold transition-all duration-300 hover:translate-y-[-3px]
              hover:bg-white/95 hover:shadow-2xl
              active:translate-y-0 active:shadow-lg
              ${isMobile ? 'px-7 py-4 text-base' : 'px-12 py-5 text-xl'}
            `}
          >
            <span className='flex items-center justify-center gap-2'>
              <Rocket
                className={`${isMobile ? 'size-4' : 'size-5'} text-orcar-060`}
              />
              Start Your Journey
              <span className='ml-2 transition-transform duration-300 group-hover:translate-x-1'>
                →
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SplitLandingPage;
